import { addDays, endOfMonth, format, Locale, parse } from 'date-fns';

export type ParseOptions = {
  locale: Locale;
  withTime: boolean;
};

export function parseDateTimeDefault(
  dateStr: string,
  referenceDate: Date,
  // @ts-ignore
  options: ParseOptions
) {
  let trimmedDateStr = dateStr.trim();
  if (trimmedDateStr === '') {
    return undefined;
  }

  // Standardní pomocí date-fns
  try {
    let date = parse(
      trimmedDateStr,
      options.withTime ? 'P p' : 'P',
      referenceDate,
      {
        locale: options.locale,
      }
    );
    if (!isNaN(date.getTime())) {
      return date;
    }
  } catch (err) {}

  return undefined;
}

/**
 * Rozdělí řetězec na datum a čas.
 * @param dateStr
 */
const splitDateTimeCs = (dateStr: string) => {
  const parts = dateStr.split(' ');
  if (parts.length === 1) {
    return [parts[0], ''];
  }
  if (parts.length === 2) {
    return parts;
  } else {
    const time = parts.pop();
    const date = parts.join(' ');
    return [date, time];
  }
};

export const isValidTimePartsCs = (parts: string[]): boolean => {
  const reg = new RegExp('^[0-9]*$');

  if (!reg.test(parts[0]) || !reg.test(parts[1])) {
    return false;
  }

  return true;
};

export const isValidTimeValueCs = (parts: string[]): boolean => {
  let hour = parseInt(parts[0]);
  let min = parts[1] !== '' ? parseInt(parts[1]) : 0;

  return hour >= 0 && hour < 24 && min >= 0 && min < 60;
};

/**
 * Převedení časových částí na Date, pro den se použije předaný referenceDate.
 * @param parts čas
 * @param referenceDate datum pro získání dne
 */
export const parseValidTimeCs = (
  parts: string[],
  referenceDate: Date
): Date | undefined => {
  const d = new Date(referenceDate);
  let hour = parseInt(parts[0]);
  let min = parts[1] !== '' ? parseInt(parts[1]) : 0;
  d.setHours(hour, min, 0, 0);
  return d || undefined;
};

export const getTimePartsCs = (value: string): string[] | undefined => {
  if (value.length > 5) {
    return undefined;
  }

  const dvojtecky = value.match(/:/g);
  if (dvojtecky && dvojtecky.length > 1) {
    return undefined;
  }

  if (!dvojtecky) {
    switch (value.length) {
      case 5:
        return undefined;
      case 4:
        return [value.substring(0, 2), value.substring(2, 4)];
      case 3:
        return [value.substring(0, 1), value.substring(1, 3)];
      case 2:
        return [value.substring(0, 2), ''];
      case 1:
        return [value.substring(0, 1), ''];
      default:
        return undefined;
    }
  } else {
    return value.split(':');
  }
};

export function parseDateTimeCs(
  inputDateStr: string,
  referenceDate: Date,
  // @ts-ignore
  options: ParseOptions
) {
  let trimmedDateStr = inputDateStr.trim();
  if (trimmedDateStr === '') {
    return undefined;
  }
  // Nahrazení , za . v trimmedDateStr
  trimmedDateStr = trimmedDateStr.replace(/,/g, '.');
  const noDiacritictLowerStr = trimmedDateStr
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

  // ---

  let dateStr, timeStr;
  if (options.withTime) {
    const parts = splitDateTimeCs(noDiacritictLowerStr);
    dateStr = parts[0];
    timeStr = parts[1] || '';
  } else {
    dateStr = noDiacritictLowerStr;
    timeStr = '';
  }
  if (!dateStr) {
    return undefined;
  }

  // ###########
  // Date
  // ###########
  let parsedDate = undefined;
  if (dateStr.indexOf('.') > 0) {
    // asi čísla oddělená tečkou

    // Pokud je na konci tečka, odebereme ji, protože chceme aby nám správně odpovídal počet part, protože jinak by pro 16.3. byly tři a to není pravda
    if (dateStr.endsWith('.')) {
      dateStr = dateStr.substring(0, dateStr.length - 1);
    }

    const parts = dateStr.split('.');

    if (parts.length === 3) {
      try {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);

        // Kontrola roku a převedení krátkého roku na dlouhý
        let year = parseInt(parts[2]);
        if (year >= 0 && year < 100) {
          year =
            year +
            referenceDate.getFullYear() -
            (referenceDate.getFullYear() % 100);
        } else if (year < 1700) {
          return undefined;
        }

        // Kontrola platnosti dne a měsíce - pokud je měsíc mimo rozsah 1-12 nebo den mimo rozsah 1-31, vrátíme undefined
        if (
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > endOfMonth(new Date(year, month - 1, 1)).getDate()
        ) {
          return undefined;
        }

        parsedDate = new Date(year, month - 1, day);
      } catch (err) {}
    } else if (parts.length === 2) {
      try {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const dt = endOfMonth(
          new Date(referenceDate.getFullYear(), month - 1, 1)
        );
        if (day <= dt.getDate()) {
          parsedDate = new Date(referenceDate.getFullYear(), month - 1, day);
        }
      } catch (err) {}
    }
  } else if (/^[0-9]+$/.test(dateStr)) {
    // jen čísla
    if (dateStr.length <= 2) {
      // jen den
      const day = parseInt(dateStr);
      const endMonth = endOfMonth(referenceDate);
      if (day > 0 && day <= endMonth.getDate()) {
        parsedDate = new Date(
          referenceDate.getFullYear(),
          referenceDate.getMonth(),
          day
        );
      }
    } else if (dateStr.length === 4) {
      // den a měsíc
      const day = parseInt(dateStr.substring(0, 2));
      const month = parseInt(dateStr.substring(2, 4));
      if (day > 0 && month > 0 && month <= 12) {
        const dt = endOfMonth(
          new Date(referenceDate.getFullYear(), month - 1, 1)
        );
        if (day <= dt.getDate()) {
          parsedDate = new Date(referenceDate.getFullYear(), month - 1, day);
        }
      }
    } else if (dateStr.length === 6) {
      // den, měsíc a "krátký" rok
      const day = parseInt(dateStr.substring(0, 2));
      const month = parseInt(dateStr.substring(2, 4));
      let year = parseInt(dateStr.substring(4, 6));
      if (year >= 0 && year < 100) {
        year =
          year +
          referenceDate.getFullYear() -
          (referenceDate.getFullYear() % 100);
      }
      if (day > 0 && month > 0 && month <= 12 && year > 1700) {
        const dt = endOfMonth(new Date(year, month - 1, 1));
        if (day <= dt.getDate()) {
          parsedDate = new Date(year, month - 1, day);
        }
      }
    } else {
      const parsed = parse(dateStr, 'ddMMyyyy', referenceDate);
      if (parsed instanceof Date && !isNaN(parsed as any)) {
        parsedDate = parsed;
      }
    }
  } else {
    let today = new Date();

    // název dne (dnes, zítra apod.)
    if ('dnes'.startsWith(dateStr)) {
      parsedDate = today;
    }

    if ('zitra'.startsWith(dateStr)) {
      parsedDate = addDays(today, 1);
    }
    if ('pozitri'.startsWith(dateStr)) {
      parsedDate = addDays(today, 2);
    }

    if ('vcera'.startsWith(dateStr)) {
      parsedDate = addDays(today, -1);
    }
    if ('predevcirem'.startsWith(dateStr)) {
      parsedDate = addDays(today, -2);
    }

    // Den v týdnu
    let dt = new Date(referenceDate.getTime());
    dt = addDays(dt, 1);
    for (let a = 0; a < 7; a++) {
      let dayName = format(dt, 'EEEE', { locale: options.locale });
      dayName = dayName
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');

      if (dayName.startsWith(dateStr)) {
        parsedDate = dt;
        break;
      }
      dt = addDays(dt, 1);
    }
  }

  // ###########
  // Time
  // ###########
  let parsedTime = undefined;
  if (options.withTime && timeStr) {
    const parts = getTimePartsCs(timeStr);
    if (parts !== undefined) {
      if (isValidTimePartsCs(parts) && isValidTimeValueCs(parts)) {
        parsedTime = parseValidTimeCs(parts, referenceDate);
      }
    }
  }
  // ###########

  // ---

  if (!parsedDate) {
    return undefined;
  }

  if (parsedTime) {
    parsedDate.setHours(parsedTime.getHours(), parsedTime.getMinutes());
  } else {
    parsedDate.setHours(0, 0, 0, 0);
  }
  return parsedDate;
}

export function parseDateTime(
  dateStr: string,
  referenceDate: Date,
  options: ParseOptions
) {
  let fn = undefined;
  switch (options.locale.code) {
    case 'cs':
      fn = parseDateTimeCs;
      break;
    default:
      fn = parseDateTimeDefault;
  }

  if (fn) {
    return fn(dateStr, referenceDate, {
      locale: options.locale,
      withTime: options.withTime,
    });
  } else {
    return undefined;
  }
}
